import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { JSONContent } from '@tiptap/core';
import { ArrowLeft, ImagePlus, Loader2, Eye, Pencil, Save, Upload, User as UserIcon } from 'lucide-react';
import { DashboardShell } from '../layout/DashboardShell';
import { UserMenu } from '../components/UserMenu';
import { DashPanel } from '../components/DashPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardProfile } from '../context/DashboardProfileContext';
import { BLOG_CATEGORY_OPTIONS, BLOG_CARD_COLOR_OPTIONS, type BlogCategory, type BlogCardColor } from '../types/blog';
import { BlockEditor } from './editor/BlockEditor';
import { BlogPostView } from './BlogPostView';
import { ArticleOutline } from './ArticleOutline';
import { extractHeadingOutline } from './editor/headingOutline';
import { compressImageToDataUrl } from './editor/imageUpload';
import { createBlogPost, getBlogPost, updateBlogPost, publishBlogPost } from './blogApi';

const EMPTY_DOC: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };

/** Deriva um nome legível do e-mail — mesmo padrão de `api/blog/posts/index.ts`,
 *  só pra prévia de um post ainda não criado (sem autorNome salvo de verdade). */
function nameFromEmail(email: string): string {
  const handle = email.split('@')[0] ?? email;
  return handle
    .replace(/[._]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function BlogEditorPage() {
  const { postId } = useParams<{ postId: string }>();
  const isEdit = Boolean(postId);
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const { profile: dashboardProfile } = useDashboardProfile();
  const coverFileRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'draft' | 'publish' | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const [titulo, setTitulo] = useState('');
  const [resumo, setResumo] = useState('');
  const [categoria, setCategoria] = useState<BlogCategory | ''>('');
  const [corCard, setCorCard] = useState<BlogCardColor>('azul');
  const [capa, setCapa] = useState('');
  const [blocos, setBlocos] = useState<JSONContent>(EMPTY_DOC);
  const [status, setStatus] = useState<'rascunho' | 'publicado'>('rascunho');
  const [publicadoEm, setPublicadoEm] = useState<number | undefined>(undefined);
  // Só pra prévia (byline) — um post existente já traz isso salvo; um post
  // novo ainda não tem, então aproxima com "Minha conta" (dashboardUsers/{uid})
  // — mesma prioridade de api/blog/posts/index.ts: perfil salvo > nome da conta > e-mail.
  const [autorNome, setAutorNome] = useState('');
  const [autorFotoUrl, setAutorFotoUrl] = useState('');
  const outline = useMemo(() => extractHeadingOutline(blocos), [blocos]);

  useEffect(() => {
    if (isEdit && postId) {
      (async () => {
        try {
          const token = await getIdToken();
          if (!token) throw new Error('Sem token de autenticação');
          const { post } = await getBlogPost(postId, token);
          setTitulo(post.titulo);
          setResumo(post.resumo);
          setCategoria(post.categoria ?? '');
          setCorCard(post.corCard ?? 'azul');
          setCapa(post.capa);
          setBlocos(post.blocos ?? EMPTY_DOC);
          setStatus(post.status);
          setPublicadoEm(post.publicadoEm);
          setAutorNome(post.autorNome);
          setAutorFotoUrl(post.autorFotoUrl ?? '');
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Falha ao carregar artigo');
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    // Novo post: aproxima a prévia com "Minha conta" (já carregada no contexto).
    setAutorNome(dashboardProfile?.nome || user?.displayName || (user?.email ? nameFromEmail(user.email) : 'Autor'));
    setAutorFotoUrl(dashboardProfile?.fotoUrl ?? '');
  }, [isEdit, postId, getIdToken, user, dashboardProfile]);

  const currentPayload = () => ({
    titulo,
    resumo,
    capa,
    blocos,
    categoria: categoria || undefined,
    corCard,
  });

  const handleCoverPick = () => coverFileRef.current?.click();

  const handleCoverChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingCover(true);
    setError(null);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      setCapa(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao processar imagem de capa');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!titulo.trim()) {
      setError('Título é obrigatório');
      return;
    }
    setSaving('draft');
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      if (isEdit && postId) {
        await updateBlogPost(postId, currentPayload(), token);
      } else {
        const { post } = await createBlogPost({ ...currentPayload(), titulo: titulo.trim() }, token);
        navigate(`/dashboard/blog/${post.id}/edit`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao salvar rascunho');
    } finally {
      setSaving(null);
    }
  };

  const handlePublish = async () => {
    if (!titulo.trim()) {
      setError('Título é obrigatório');
      return;
    }
    setSaving('publish');
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Sem token de autenticação');
      let id = postId;
      if (!isEdit) {
        const { post } = await createBlogPost({ ...currentPayload(), titulo: titulo.trim() }, token);
        id = post.id;
      }
      if (!id) throw new Error('Post sem id');
      await publishBlogPost(id, currentPayload(), token);
      navigate('/dashboard/blog');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao publicar artigo');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <DashboardShell mode="blog">
        <div className="flex-1 flex items-center justify-center text-muted-foreground gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando artigo…
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell mode="blog">
      <header className="shrink-0 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/dashboard/blog')}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar
          </Button>
          <div>
            <h1 className="text-base font-semibold text-foreground">{isEdit ? 'Editar artigo' : 'Novo artigo'}</h1>
            {status === 'publicado' && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">Publicado</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPreviewing((v) => !v)}>
            {previewing ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {previewing ? 'Editar' : 'Visualizar'}
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={saving !== null} onClick={handleSaveDraft}>
            {saving === 'draft' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Salvar rascunho
          </Button>
          <Button type="button" size="sm" disabled={saving !== null} onClick={handlePublish}>
            {saving === 'publish' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Publicar
          </Button>
          <UserMenu />
        </div>
      </header>

      {/* p-6 fica num wrapper interno, não no elemento que rola — se o padding
          estivesse aqui, o toolbar sticky do editor grudaria com esse espaço
          em branco acima dele (padding não "some" ao rolar), deixando uma
          folga entre o nav e o sticky em vez de ficar colado nele. */}
      <div className="flex-1 overflow-y-auto bg-blue-100 dark:bg-background border-t border-l border-border rounded-tl-2xl">
        <div className="p-6">
        {error && (
          <DashPanel padding="sm" className="mb-4 border-destructive/40 bg-destructive/5 text-sm text-destructive max-w-3xl">
            {error}
          </DashPanel>
        )}

        {previewing ? (
          // reading-surface (não o azulado do shell do dashboard) — representa
          // como a página pública vai aparecer de verdade, tema claro/escuro
          // incluso (mesmo estilo de src/pages/BlogPost.tsx).
          <div className="max-w-4xl mx-auto rounded-2xl reading-surface p-8 md:p-12 shadow-sm">
            <div className="lg:grid lg:grid-cols-[1fr_200px] lg:gap-16 lg:items-start">
              <article className="min-w-0">
                {categoria && (
                  <p className="font-reading text-xs font-semibold uppercase tracking-widest text-reading/50 mb-3">
                    {BLOG_CATEGORY_OPTIONS.find((c) => c.id === categoria)?.nome}
                  </p>
                )}
                <h1 className="font-reading text-2xl md:text-4xl font-bold text-reading leading-[1.2] mb-6">
                  {titulo || 'Sem título'}
                </h1>

                <div className="flex items-center gap-3">
                  {autorFotoUrl ? (
                    <img src={autorFotoUrl} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-reading/10 flex items-center justify-center shrink-0">
                      <UserIcon className="h-5 w-5 text-reading/50" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wide text-reading/80 truncate">
                      {autorNome || 'Autor'}
                    </p>
                    <p className="text-xs text-reading/50 uppercase tracking-wide mt-0.5">
                      {new Date(publicadoEm ?? Date.now())
                        .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
                        .replace('.', '')}
                    </p>
                  </div>
                </div>

                <div className="h-px bg-reading/15 mt-6 mb-10" />

                <BlogPostView blocos={blocos} className="prose-reading prose-lg font-reading text-reading" />
              </article>

              <ArticleOutline items={outline} className="hidden lg:block" />
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-5">
            <DashPanel className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="titulo">Título</Label>
                <Input id="titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título do artigo" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resumo">Resumo</Label>
                <Textarea
                  id="resumo"
                  value={resumo}
                  onChange={(e) => setResumo(e.target.value)}
                  placeholder="Prévia curta mostrada na listagem do blog"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select
                    value={categoria || '_nenhuma'}
                    onValueChange={(v) => setCategoria(v === '_nenhuma' ? '' : (v as BlogCategory))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sem categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_nenhuma">Sem categoria</SelectItem>
                      {BLOG_CATEGORY_OPTIONS.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Imagem de capa</Label>
                  <input
                    ref={coverFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleCoverChange}
                  />
                  <Button type="button" variant="outline" className="w-full justify-start gap-2" disabled={uploadingCover} onClick={handleCoverPick}>
                    {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                    {uploadingCover ? 'Processando…' : capa ? 'Trocar imagem' : 'Escolher imagem'}
                  </Button>
                </div>
              </div>
              {capa && <img src={capa} alt="" className="w-full max-w-md h-40 object-cover rounded-xl border border-border" />}

              <div className="space-y-2">
                <Label>Cor do card na listagem</Label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {BLOG_CARD_COLOR_OPTIONS.map((cor) => (
                      <button
                        key={cor.id}
                        type="button"
                        title={cor.nome}
                        onClick={() => setCorCard(cor.id)}
                        className={cn(
                          'h-7 w-7 rounded-full transition-transform',
                          cor.className,
                          corCard === cor.id ? 'ring-2 ring-offset-2 ring-foreground scale-110' : 'hover:scale-105'
                        )}
                      />
                    ))}
                  </div>
                  {/* Prévia rápida do bloco do card — a "Visualizar" acima mostra a
                      página do artigo (não a listagem), então sem isso não daria
                      pra ver o resultado sem sair do editor. */}
                  <div className={cn('rounded-lg px-3 py-2 max-w-[220px]', BLOG_CARD_COLOR_OPTIONS.find((c) => c.id === corCard)?.className)}>
                    <p className="text-[11px] font-medium text-primary-foreground/70 truncate">
                      {categoria ? BLOG_CATEGORY_OPTIONS.find((c) => c.id === categoria)?.nome : 'Categoria'}
                    </p>
                    <p className="text-xs font-semibold text-primary-foreground truncate">
                      {titulo || 'Título do artigo'}
                    </p>
                  </div>
                </div>
              </div>
            </DashPanel>

            <BlockEditor content={blocos} onChange={setBlocos} />
          </div>
        )}
        </div>
      </div>
    </DashboardShell>
  );
}
